import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { FeatureProvider } from './contexts/FeatureContext';
import { OfflineProvider } from './contexts/OfflineContext';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import { Toaster } from './components/ui/sonner';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import ErrorBoundary from './components/ErrorBoundary';
import '@/App.css';

// =============================================================================
// SERVICE WORKER REGISTRATION
// =============================================================================
const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('[PWA] Service Worker registered:', registration.scope);
      
      // Handle updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New version available - could prompt user to refresh
            console.log('[PWA] New version available');
          }
        });
      });
    } catch (error) {
      console.error('[PWA] Service Worker registration failed:', error);
    }
  }
};

// =============================================================================
// LOADING COMPONENT
// =============================================================================
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-slate-600 dark:text-slate-400">Loading...</p>
    </div>
  </div>
);

// =============================================================================
// EAGER LOADED - Core pages needed immediately
// =============================================================================
import LoginPage from './pages/LoginPage';
import FirstRunSetupPage from './pages/FirstRunSetupPage';
import DashboardPage from './pages/DashboardPage';
import Layout from './components/Layout';

// =============================================================================
// LAZY LOADED - Split into chunks, loaded on demand
// =============================================================================

// Auth pages (rarely revisited after login)
const SetupAccountPage = lazy(() => import('./pages/SetupAccountPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));

// Core FCA pages
const FacilitiesPage = lazy(() => import('./pages/FacilitiesPage'));
const FacilityDetailPage = lazy(() => import('./pages/FacilityDetailPage'));
const AssessmentPage = lazy(() => import('./pages/AssessmentPage'));
const AssessmentsListPage = lazy(() => import('./pages/AssessmentsListPage'));
const ReviewQueuePage = lazy(() => import('./pages/ReviewQueuePage'));

// Client pages
const ClientsPage = lazy(() => import('./pages/ClientsPage'));
const ClientDetailPage = lazy(() => import('./pages/ClientDetailPage'));

// Map pages (heavy - includes Leaflet)
const PortfolioMapPage = lazy(() => import('./pages/PortfolioMapPage'));
const GISMapPage = lazy(() => import('./pages/GISMapPage'));
const WorkOrderMapPage = lazy(() => import('./pages/WorkOrderMapPage'));

// IWMS pages
const WorkOrdersPage = lazy(() => import('./pages/WorkOrdersPage'));
const WorkOrderDetailPage = lazy(() => import('./pages/WorkOrderDetailPage'));
const PMSchedulesPage = lazy(() => import('./pages/PMSchedulesPage'));
const VendorsPage = lazy(() => import('./pages/VendorsPage'));
const WarrantiesPage = lazy(() => import('./pages/WarrantiesPage'));
const LeasesPage = lazy(() => import('./pages/LeasesPage'));
const AssetsPage = lazy(() => import('./pages/AssetsPage'));
const SchedulerPage = lazy(() => import('./pages/SchedulerPage'));
const InternalAssetDetailPage = lazy(() => import('./pages/InternalAssetDetailPage'));
const TechnicianMobilePage = lazy(() => import('./pages/TechnicianMobilePage'));
const SupervisorLiveMapPage = lazy(() => import('./pages/SupervisorLiveMapPage'));
const MessagingPage = lazy(() => import('./pages/MessagingPage'));

// Floor Plans (heavy - includes react-pdf)
const FloorPlansPage = lazy(() => import('./pages/FloorPlansPage'));

// Admin pages
const AdminPage = lazy(() => import('./pages/AdminPage'));
const OrganizationPage = lazy(() => import('./pages/OrganizationPage'));
const IWMSAdminPage = lazy(() => import('./pages/IWMSAdminPage'));
const HealthDashboardPage = lazy(() => import('./pages/HealthDashboardPage'));

// Documentation pages (heavy - includes jspdf)
const SystemDocumentationPage = lazy(() => import('./pages/SystemDocumentationPage'));
const DocumentViewerPage = lazy(() => import('./pages/DocumentViewerPage'));

// Analysis pages
const InspectionPlannerPage = lazy(() => import('./pages/InspectionPlannerPage'));
const RegionalAnalysisPage = lazy(() => import('./pages/RegionalAnalysisPage'));
const ReportGeneratorPage = lazy(() => import('./pages/ReportGeneratorPage'));
const PresentationViewPage = lazy(() => import('./pages/PresentationViewPage'));

// Public pages
const RequestPage = lazy(() => import('./pages/RequestPage'));
const RequestStatusPage = lazy(() => import('./pages/RequestStatusPage'));
const AssetDetailPage = lazy(() => import('./pages/AssetDetailPage'));
const FinanceHubPage = lazy(() => import('./pages/FinanceHubPage'));

// AI Dashboard (Natural language portfolio queries)
const AIDashboardPage = lazy(() => import('./pages/AIDashboardPage'));

// =============================================================================
// PRIVATE ROUTE WRAPPER
// =============================================================================
const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  return user ? children : <Navigate to="/login" />;
};

// =============================================================================
// LAZY ROUTE WRAPPER - Combines Suspense with Layout
// =============================================================================
const LazyRoute = ({ children, withLayout = true }) => {
  const content = (
    <Suspense fallback={<PageLoader />}>
      {children}
    </Suspense>
  );

  if (withLayout) {
    return (
      <PrivateRoute>
        <Layout>{content}</Layout>
      </PrivateRoute>
    );
  }

  return <PrivateRoute>{content}</PrivateRoute>;
};

// Public route with suspense (no auth)
const PublicLazyRoute = ({ children }) => (
  <Suspense fallback={<PageLoader />}>
    {children}
  </Suspense>
);

// =============================================================================
// APP ROUTES
// =============================================================================
function AppRoutes() {
  return (
    <Routes>
      {/* Public Request Portal Routes (no auth required) */}
      <Route path="/request" element={<PublicLazyRoute><RequestPage /></PublicLazyRoute>} />
      <Route path="/request/:accessCode" element={<PublicLazyRoute><RequestPage /></PublicLazyRoute>} />
      <Route path="/request/status/:trackingNumber" element={<PublicLazyRoute><RequestStatusPage /></PublicLazyRoute>} />
      <Route path="/request/status" element={<PublicLazyRoute><RequestStatusPage /></PublicLazyRoute>} />
      <Route path="/asset/:assetId" element={<PublicLazyRoute><AssetDetailPage /></PublicLazyRoute>} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/setup" element={<FirstRunSetupPage />} />
      <Route path="/setup-account" element={<PublicLazyRoute><SetupAccountPage /></PublicLazyRoute>} />
      <Route path="/reset-password" element={<PublicLazyRoute><ResetPasswordPage /></PublicLazyRoute>} />
      
      {/* Dashboard - Eager loaded for fast initial render */}
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </PrivateRoute>
        }
      />
      
      {/* Client Routes */}
      <Route path="/clients" element={<LazyRoute><ClientsPage /></LazyRoute>} />
      <Route path="/clients/:clientId" element={<LazyRoute><ClientDetailPage /></LazyRoute>} />
      
      {/* Facility Routes */}
      <Route path="/facilities" element={<LazyRoute><FacilitiesPage /></LazyRoute>} />
      <Route path="/facilities/:facilityId" element={<LazyRoute><FacilityDetailPage /></LazyRoute>} />
      
      {/* Assessment Routes */}
      <Route path="/assessments" element={<LazyRoute><AssessmentsListPage /></LazyRoute>} />
      <Route path="/assessments/:assessmentId" element={<LazyRoute><AssessmentPage /></LazyRoute>} />
      <Route path="/review-queue" element={<LazyRoute><ReviewQueuePage /></LazyRoute>} />
      
      {/* IWMS Routes */}
      <Route path="/work-orders" element={<LazyRoute><WorkOrdersPage /></LazyRoute>} />
      <Route path="/work-orders/:workOrderId" element={<LazyRoute><WorkOrderDetailPage /></LazyRoute>} />
      <Route path="/messaging" element={<LazyRoute><MessagingPage /></LazyRoute>} />
      <Route path="/pm-schedules" element={<LazyRoute><PMSchedulesPage /></LazyRoute>} />
      <Route path="/vendors" element={<LazyRoute><VendorsPage /></LazyRoute>} />
      <Route path="/warranties" element={<LazyRoute><WarrantiesPage /></LazyRoute>} />
      <Route path="/leases" element={<LazyRoute><LeasesPage /></LazyRoute>} />
      <Route path="/iwms-assets" element={<LazyRoute><AssetsPage /></LazyRoute>} />
      <Route path="/assets/:assetId" element={<LazyRoute><InternalAssetDetailPage /></LazyRoute>} />
      <Route path="/scheduler" element={<LazyRoute><SchedulerPage /></LazyRoute>} />
      <Route path="/technician" element={<LazyRoute withLayout={false}><TechnicianMobilePage /></LazyRoute>} />
      <Route path="/live-map" element={<LazyRoute><SupervisorLiveMapPage /></LazyRoute>} />
      
      {/* Map Routes (Heavy - Leaflet) */}
      <Route path="/portfolio-map" element={<LazyRoute><PortfolioMapPage /></LazyRoute>} />
      <Route path="/work-order-map" element={<LazyRoute><WorkOrderMapPage /></LazyRoute>} />
      <Route path="/gis-map" element={<LazyRoute><GISMapPage /></LazyRoute>} />
      
      {/* Floor Plans (Heavy - react-pdf) */}
      <Route path="/floor-plans" element={<LazyRoute><FloorPlansPage /></LazyRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<LazyRoute><AdminPage /></LazyRoute>} />
      <Route path="/organization" element={<LazyRoute><OrganizationPage /></LazyRoute>} />
      <Route path="/iwms-admin" element={<LazyRoute><IWMSAdminPage /></LazyRoute>} />
      <Route path="/health-dashboard" element={<LazyRoute withLayout={false}><HealthDashboardPage /></LazyRoute>} />
      <Route path="/finance-hub" element={<LazyRoute><FinanceHubPage /></LazyRoute>} />
      <Route path="/ai-dashboard" element={<LazyRoute><AIDashboardPage /></LazyRoute>} />
      
      {/* Documentation Routes (Heavy - jspdf) */}
      <Route path="/system-docs" element={<LazyRoute withLayout={false}><SystemDocumentationPage /></LazyRoute>} />
      <Route path="/system-docs/:docSlug" element={<LazyRoute withLayout={false}><DocumentViewerPage /></LazyRoute>} />
      
      {/* Analysis Routes */}
      <Route path="/inspection-planner" element={<LazyRoute><InspectionPlannerPage /></LazyRoute>} />
      <Route path="/regional-analysis" element={<LazyRoute><RegionalAnalysisPage /></LazyRoute>} />
      <Route path="/assessments/:assessmentId/report" element={<LazyRoute><ReportGeneratorPage /></LazyRoute>} />
      <Route path="/assessments/:assessmentId/presentation/:reportId" element={<LazyRoute><PresentationViewPage /></LazyRoute>} />
    </Routes>
  );
}

// =============================================================================
// APP ENTRY
// =============================================================================
function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <FeatureProvider>
          <OfflineProvider>
            <ImpersonationProvider>
              <BrowserRouter>
                <ErrorBoundary message="The application encountered an error. Please try reloading the page.">
                  <ImpersonationBanner />
                  <div className="App">
                    <AppRoutes />
                    <Toaster position="top-right" />
                  </div>
                </ErrorBoundary>
              </BrowserRouter>
            </ImpersonationProvider>
          </OfflineProvider>
        </FeatureProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
